import type { ApprovalStepDraft } from 'ui-product-approvals-common/types/ApprovalData';
import {
  countDistinctApprovers,
  hasEmptyStep,
  hasHumanApprover,
} from './approvalStepsUtils';

// The maximum number of distinct approvers allowed across the ENTIRE approval
// (not per stage). Single source of truth shared by the request modal, the
// reassign modal, and the settings template builder.
export const MAX_APPROVERS = 10;

// The maximum number of stages allowed in a single approval process. Single
// source of truth shared by the request modal, the reassign modal, and the
// settings template builder.
export const MAX_STAGES = 10;

export interface ApprovalProcessValidation {
  /** At least one USER or TEAM approver exists somewhere in the process. */
  hasHumanApprover: boolean;
  /** Some stage has no approvers. */
  hasEmptyStep: boolean;
  /** Distinct approver count across all stages. */
  distinctApproverCount: number;
  /** Distinct approver count exceeds MAX_APPROVERS. */
  exceedsMaxApprovers: boolean;
  /** Distinct approver count has reached MAX_APPROVERS (adding more is blocked). */
  isAtMaxApprovers: boolean;
  /** Stage count has reached MAX_STAGES (adding more is blocked). */
  isAtMaxStages: boolean;
  /** True when the process is a valid, submittable approval configuration. */
  isValid: boolean;
}

/**
 * Centralized "is this a valid approval process" check, shared across the
 * request, reassign, and template flows so they agree on the common core rules:
 * at least one human approver, no empty stage, at least one approver, and no
 * more than MAX_APPROVERS distinct approvers total. Each flow layers its own
 * extra constraints (e.g. reassign's already-approved rules) on top.
 */
export const validateApprovalProcess = (
  steps: ApprovalStepDraft[]
): ApprovalProcessValidation => {
  const distinctApproverCount = countDistinctApprovers(steps);
  const humanApprover = hasHumanApprover(steps);
  const emptyStep = hasEmptyStep(steps);
  const exceedsMaxApprovers = distinctApproverCount > MAX_APPROVERS;

  return {
    hasHumanApprover: humanApprover,
    hasEmptyStep: emptyStep,
    distinctApproverCount,
    exceedsMaxApprovers,
    isAtMaxApprovers: distinctApproverCount >= MAX_APPROVERS,
    isAtMaxStages: steps.length >= MAX_STAGES,
    isValid:
      distinctApproverCount > 0 &&
      humanApprover &&
      !emptyStep &&
      !exceedsMaxApprovers,
  };
};
